import { Component, EventEmitter, inject, OnInit, Output, computed, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuItem } from 'primeng/api';
import { BreadcrumbService } from '../../../core/services/breadcrumb.service';
import { EditorStateService } from '../../../core/services/editor-state.service';
import { StationService } from '../../../core/services/station.service';
import { KeycloakService } from 'usermgmt';

@Component({
  selector: 'app-header',
  imports: [ButtonModule, MenuModule, BreadcrumbModule],
  templateUrl: './header.html',
 })
export class Header implements OnInit {
  protected breadcrumbService = inject(BreadcrumbService);
  protected editorState = inject(EditorStateService);
  private stationService: any = inject(StationService);
  private router = inject(Router);
  private keycloakService = inject(KeycloakService);

  constructor() {
    this.checkAndSetUserMgmtCrumbs(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.checkAndSetUserMgmtCrumbs(event.urlAfterRedirects);
    });
  }

  private checkAndSetUserMgmtCrumbs(url: string) {
    if (url.includes('/usermgmt/customer/addCustomer')) {
      this.breadcrumbService.setCrumbs(['Home', 'User Management', 'Organizations', 'Add/Edit Organization']);
    } else if (url.includes('/usermgmt/customer')) {
      this.breadcrumbService.setCrumbs(['Home', 'User Management', 'Organizations']);
    } else if (url.includes('/usermgmt/applications/assignResource')) {
      this.breadcrumbService.setCrumbs(['Home', 'User Management', 'Applications', 'Assign Resource']);
    } else if (url.includes('/usermgmt/applications')) {
      this.breadcrumbService.setCrumbs(['Home', 'User Management', 'Applications']);
    } else if (url.includes('/usermgmt/roles/config')) {
      this.breadcrumbService.setCrumbs(['Home', 'User Management', 'Roles', 'Configure Role']);
    } else if (url.includes('/usermgmt/roles')) {
      this.breadcrumbService.setCrumbs(['Home', 'User Management', 'Roles']);
    } else if (url.includes('/usermgmt/users/config')) {
      this.breadcrumbService.setCrumbs(['Home', 'User Management', 'Users', 'Configure User']);
    } else if (url.includes('/usermgmt/users')) {
      this.breadcrumbService.setCrumbs(['Home', 'User Management', 'Users']);
    }
  }

  menuItems = computed(() => {
    const crumbs = this.breadcrumbService.crumbsSignal();

    // Hide 'Home' if the user is in the Digital Library or User Management modules
    if (crumbs.length >= 2 && crumbs[0] === 'Home' && (crumbs[1] === 'Digital Library' || crumbs[1] === 'User Management')) {
      return crumbs.slice(1).map(crumb => ({
        label: crumb,
        command: () => this.onCrumbClick(crumb)
      }));
    }

    return crumbs.map(crumb => ({
      label: crumb,
      command: () => this.onCrumbClick(crumb)
    }));
  });

  ngOnInit() { }

  onCrumbClick(crumb: string) {
    this.breadcrumbService.emitClick(crumb);

    const text = crumb.trim().toLowerCase();
    if (text === 'home') {
      this.router.navigate(['/dashboard']);
      return;
    } else if (text === 'digital library' || text === 'digital-library') {
      this.router.navigate(['/digital-library']);
      return;
    } else if (text === 'user management') {
      this.router.navigate(['/usermgmt/customer']);
      return;
    } else if (text === 'organizations') {
      this.router.navigate(['/usermgmt/customer']);
      return;
    } else if (text === 'applications') {
      this.router.navigate(['/usermgmt/applications']);
      return;
    } else if (text === 'roles') {
      this.router.navigate(['/usermgmt/roles']);
      return;
    } else if (text === 'users') {
      this.router.navigate(['/usermgmt/users']);
      return;
    }

    const navigateToStation = (stationsList: any[]) => {
      // 1. Exact match by name or code
      let matchedStation = stationsList.find(
        (s: any) => s.name.toLowerCase() === text ||
          s.code.toLowerCase() === text
      );

      // 2. Fallback: substring/includes match (e.g., 'Ongole' matches 'Ongole City')
      if (!matchedStation) {
        matchedStation = stationsList.find(
          (s: any) => s.name.toLowerCase().includes(text) ||
            text.includes(s.name.toLowerCase())
        );
      }

      // 3. Fallback: junction/hub suffix replacement
      if (!matchedStation) {
        matchedStation = stationsList.find(
          (s: any) => s.name.toLowerCase().replace('junction', '').trim() === text ||
            `${s.name} hub`.toLowerCase() === text
        );
      }

      if (matchedStation) {
        this.router.navigate(['/station-data', matchedStation.code], {
          queryParams: { id: matchedStation.id }
        });
      } else {
        console.warn('No matched station found for crumb:', crumb);
      }
    };

    const currentStations = this.stationService.stationsSignal();
    if (currentStations && currentStations.length > 0) {
      navigateToStation(currentStations);
    } else {
      this.stationService.getStations().subscribe({
        next: (loadedStations: any[]) => {
          navigateToStation(loadedStations);
        },
        error: (err: any) => {
          console.error('Failed to load stations for breadcrumb navigation:', err);
        }
      });
    }
  }

  @Output() toggleSidebar = new EventEmitter<void>();

  items = [
    {
      label: 'Profile',
      icon: 'ti ti-user',
      disabled: true,
      command: () => {
      }
    },
    {
      label: 'Settings',
      icon: 'ti ti-settings',
      disabled: true,
      command: () => {
      }
    },
    {
      label: 'Logout',
      icon: 'ti ti-logout',
      command: () => {
        this.keycloakService.logout();
      }
    }
  ];
}
